import { Icon, Icons } from '@/components/icon';
import { Avatar, Chip, type ChipSize, Glyph } from '@/components/ui/design-system';
import { Popover, PopoverContent, PopoverScrollArea } from '@/components/ui/popover';
import { P3 } from '@/components/ui/text';
import { useResolvedMediaUrl } from '@/hooks/use-resolved-media-url';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import type { components } from '@/services/api-allo/api-allo-v1';
import { $apiAllo } from '@/services/api-allo/client';
import { PopoverAnchor } from '@radix-ui/react-popover';
import * as React from 'react';
import { TableCell } from '../table';

type CompanyResponse = components['schemas']['CompanyResponse'];

// Company chip component - displays a company with avatar and name
export function Company({
  company,
  underlined = true,
  className,
  tabIndex,
  onClick,
}: {
  company: CompanyResponse;
  underlined?: boolean;
  className?: string;
  tabIndex?: number;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const handleMouseDown = (e: React.MouseEvent) => {
    // Prevent the button from stealing focus, but focus the parent cell
    e.preventDefault();
    const cell = (e.target as HTMLElement).closest('[data-cell-row]') as HTMLElement | null;
    cell?.focus();
  };

  const isClickable = onClick !== undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={isClickable ? handleMouseDown : undefined}
      tabIndex={tabIndex}
      className={cn(
        'group/company flex flex-none flex-col rounded-xs px-1 py-0.5 outline-none hover:bg-primary-5',
        isClickable && 'cursor-pointer  focus:bg-primary-5',
        className,
      )}
    >
      <div className="flex items-center gap-1">
        <CompanyAvatar imageUrl={company.image_url} size={16} />
        <div className="flex flex-col relative">
          <P3 className="text-primary-100">{company.name}</P3>
          {underlined && (
            <span className="h-px w-full bg-primary-10 group-hover/company:opacity-0" />
          )}
        </div>
      </div>
    </button>
  );
}

// Legacy pixel sizes used by call-sites (16 / 18 / 28) snap to the closest
// DS Chip size. New code should pass `dsSize` directly.
const pxToDsSize = (size: number): ChipSize => {
  if (size <= 16) {
    return 'sm';
  }
  if (size <= 22) {
    return 'md';
  }
  return 'lg';
};

// Company avatar — Chip containing the company logo when available, falling
// back to a building-office icon glyph.
export function CompanyAvatar({
  imageUrl,
  size = 16,
}: {
  imageUrl?: string | null;
  size?: number;
}) {
  const resolvedUrl = useResolvedMediaUrl(imageUrl ?? undefined);
  const dsSize = pxToDsSize(size);

  return (
    <Chip size={dsSize} color="primary">
      {resolvedUrl ? (
        <Avatar type="image" src={resolvedUrl} name="Company" />
      ) : (
        <Glyph name={Icons.BuildingOffice} />
      )}
    </Chip>
  );
}

// Company row in the popover list
function CompanyRow({
  company,
  isSelected,
  onClick,
}: {
  company: CompanyResponse;
  isSelected?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full cursor-pointer items-center justify-between overflow-hidden rounded-md px-1 py-1 hover:bg-primary-3',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-1 px-1 py-0.5">
        <CompanyAvatar imageUrl={company.image_url} size={16} />
        <P3 className="min-w-0 truncate text-primary-100">{company.name}</P3>
      </div>
      {isSelected && (
        <Icon name={Icons.CircleCheck} className="text-m shrink-0 text-highlight-default" />
      )}
    </button>
  );
}

// Company popover for selecting/editing companies
export function CompanyPopover({
  children,
  company,
  open: controlledOpen,
  onOpenChange,
  onSelectCompany,
  onCreateCompany,
  collisionBoundary,
  align = 'start',
  side,
  sideOffset = -41,
}: {
  children: React.ReactNode;
  company?: CompanyResponse | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSelectCompany?: (company: CompanyResponse | null) => void;
  onCreateCompany?: (name: string) => void;
  collisionBoundary?: Element | Element[] | null;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
}) {
  const { t } = useTranslation('desktop_ui');
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Debounce search value - only update after 300ms and when 3+ characters
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue.length >= 3 || searchValue.length === 0) {
        setDebouncedSearch(searchValue);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = React.useCallback(
    (value: boolean) => {
      if (isControlled) {
        onOpenChange?.(value);
      } else {
        setInternalOpen(value);
      }
    },
    [isControlled, onOpenChange],
  );

  // Fetch companies with search filter (debounced, min 3 chars)
  const companiesQuery = $apiAllo.useQuery(
    'get',
    '/v1/company',
    {
      params: {
        query: {
          'page[size]': 20,
          'filter[name]': debouncedSearch || undefined,
        },
      },
    },
    {
      enabled: open,
      staleTime: 30_000,
      placeholderData: (prev) => prev,
    },
  );

  const companies = companiesQuery.data?.data ?? [];

  // Reset search when popover closes
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (!open && prevOpenRef.current) {
      setSearchValue('');
      setDebouncedSearch('');
    }
    prevOpenRef.current = open;
  }, [open]);

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      if (newOpen === open) {
        return;
      }
      setOpen(newOpen);
      if (!newOpen) {
        const cell = triggerRef.current?.closest('[tabindex]') as HTMLElement | null;
        if (cell) {
          requestAnimationFrame(() => {
            cell.focus();
          });
        }
      }
    },
    [setOpen, open],
  );

  const handleSelectCompany = (selectedCompany: CompanyResponse) => {
    onSelectCompany?.(selectedCompany);
    setOpen(false);
  };

  const handleCreateCompany = () => {
    onCreateCompany?.(searchValue.trim());
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverAnchor
        onDoubleClick={() => {
          setOpen(true);
        }}
        ref={triggerRef}
      >
        {children}
      </PopoverAnchor>
      <PopoverContent
        align={align}
        side={side}
        sideOffset={sideOffset}
        collisionBoundary={collisionBoundary}
        className="z-popover w-[var(--radix-popover-trigger-width)] min-w-[280px] overflow-hidden rounded-2xl border border-primary-5 bg-white p-0 shadow-lg"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        {/* Search input */}
        <div className="flex h-7 items-center gap-1 px-2 py-1">
          <Icon name={Icons.MagnifyingGlass} className="shrink-0 text-primary-50 text-s" />
          <input
            ref={inputRef}
            type="text"
            placeholder={t('components.contacts.table.searchCompanies')}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            className="flex-1 bg-transparent font-inter font-medium text-primary-100 text-s caret-highlight-default outline-none placeholder:text-primary-60"
          />
        </div>

        {/* Companies list */}
        {companies.length > 0 ? (
          <>
            <div className="h-px w-full rounded-xs bg-primary-10" />
            <PopoverScrollArea className="max-h-[160px]">
              <div className="px-1 py-1">
                {companies.map((c) => (
                  <CompanyRow
                    key={c.id}
                    company={c}
                    isSelected={company?.id === c.id}
                    onClick={() => handleSelectCompany(c)}
                  />
                ))}
              </div>
            </PopoverScrollArea>
          </>
        ) : companiesQuery.isLoading || (searchValue.length > 0 && searchValue.length < 3) ? (
          <>
            <div className="h-px w-full rounded-xs bg-primary-10" />
            <div className="px-1 py-1">
              <div className="py-4 text-center font-inter font-medium text-primary-60 text-s">
                {t('components.contacts.table.loading')}
              </div>
            </div>
          </>
        ) : null}

        {/* Create company action */}
        {onCreateCompany && (
          <>
            <div className="h-px w-full rounded-xs bg-primary-10" />
            <button
              type="button"
              onClick={handleCreateCompany}
              className="flex h-7 w-full cursor-pointer items-center gap-1 px-2 py-1 hover:bg-primary-3"
            >
              <Icon name={Icons.Plus} className="text-primary-80 text-s" />
              <P3 className="text-primary-90">{t('components.contacts.table.createCompany')}</P3>
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Wrapper to isolate popover state from parent re-renders
function EditableCompanyCell({
  children,
  company,
  isOpen,
  onOpenChange,
  onSelectCompany,
  onCreateCompany,
}: {
  children: React.ReactNode;
  company?: CompanyResponse | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectCompany?: (company: CompanyResponse | null) => void;
  onCreateCompany?: (name: string) => void;
}) {
  return (
    <CompanyPopover
      company={company}
      open={isOpen}
      onOpenChange={onOpenChange}
      onSelectCompany={onSelectCompany}
      onCreateCompany={onCreateCompany}
    >
      {children}
    </CompanyPopover>
  );
}

export interface CompanyCellHandle {
  open: () => void;
}

export const CompanyCell = React.memo(
  React.forwardRef<
    CompanyCellHandle,
    {
      contactId?: string;
      company?: CompanyResponse | null;
      isOpen?: boolean;
      onOpenChange?: (open: boolean) => void;
      /** Stable callback - receives contactId to avoid closures */
      onSelectCompany?: (contactId: string, company: CompanyResponse | null) => void;
      onCreateCompany?: (name: string) => void;
      /** Callback when clicking on the company chip (e.g. to open sidepanel) */
      onCompanyClick?: (companyId: string, companyName: string) => void;
    }
  >(function CompanyCell(
    {
      contactId,
      company,
      isOpen: controlledIsOpen,
      onOpenChange,
      onSelectCompany,
      onCreateCompany,
      onCompanyClick,
    },
    ref,
  ) {
    const [internalIsOpen, setInternalIsOpen] = React.useState(false);

    // Truncation expansion state
    const [isFocused, setIsFocused] = React.useState(false);
    const [isTruncated, setIsTruncated] = React.useState(false);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    // Use controlled state if provided, otherwise use internal state
    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
    const setIsOpen = React.useCallback(
      (open: boolean) => {
        if (isControlled) {
          onOpenChange?.(open);
        } else {
          setInternalIsOpen(open);
        }
      },
      [isControlled, onOpenChange],
    );

    React.useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setIsOpen(true);
        },
      }),
      [setIsOpen],
    );

    // Wrap callback to include contactId for the stable parent callback
    const handleSelectCompany = React.useCallback(
      (selectedCompany: CompanyResponse | null) => {
        if (contactId) {
          onSelectCompany?.(contactId, selectedCompany);
        }
      },
      [contactId, onSelectCompany],
    );

    const handleCompanyClick = React.useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (company?.id && company?.name) {
          onCompanyClick?.(company.id, company.name);
        }
      },
      [company?.id, company?.name, onCompanyClick],
    );

    // Measure content to detect if it's truncated
    const checkTruncation = React.useCallback(() => {
      const content = contentRef.current;
      if (!content) {
        setIsTruncated(false);
        return;
      }

      // Check all child elements that might have truncation
      const allElements = content.querySelectorAll('*');
      for (const el of allElements) {
        if (el instanceof HTMLElement && el.scrollWidth > el.clientWidth) {
          setIsTruncated(true);
          return;
        }
      }

      setIsTruncated(false);
    }, []);

    // Listen for focus events on the parent cell wrapper (which has data-cell-row attribute)
    React.useEffect(() => {
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }

      // Find the parent cell wrapper
      const cellWrapper = wrapper.closest('[data-cell-row]') as HTMLElement | null;
      if (!cellWrapper) {
        return;
      }

      const handleFocusIn = () => {
        setIsFocused(true);
        requestAnimationFrame(checkTruncation);
      };

      const handleFocusOut = (e: FocusEvent) => {
        // Only clear focus if focus is leaving the cell entirely
        if (!cellWrapper.contains(e.relatedTarget as Node)) {
          setIsFocused(false);
          setIsTruncated(false);
        }
      };

      cellWrapper.addEventListener('focusin', handleFocusIn);
      cellWrapper.addEventListener('focusout', handleFocusOut);

      return () => {
        cellWrapper.removeEventListener('focusin', handleFocusIn);
        cellWrapper.removeEventListener('focusout', handleFocusOut);
      };
    }, [checkTruncation]);

    // Check if we should show expanded content
    const shouldExpand = isFocused && isTruncated && !isOpen;

    // Elevate parent cell z-index when expanded to escape stacking context
    // This is needed because getCommonPinningStyles sets z-index: 0 for non-pinned columns
    React.useEffect(() => {
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }

      const cellWrapper = wrapper.closest('[data-cell-row]') as HTMLElement | null;
      if (!cellWrapper) {
        return;
      }

      if (shouldExpand) {
        // Use z-index 15 for non-pinned columns (matching table-body-cell expansion logic)
        cellWrapper.style.zIndex = '15';
      } else {
        // Reset to let the inline style from getCommonPinningStyles take over
        cellWrapper.style.zIndex = '';
      }

      return () => {
        if (cellWrapper) {
          cellWrapper.style.zIndex = '';
        }
      };
    }, [shouldExpand]);

    const cellContent = (
      <>
        {/* Base cell for measuring truncation */}
        <div ref={contentRef}>
          <TableCell className="overflow-hidden px-2">
            {company ? (
              <Company
                company={company}
                tabIndex={-1}
                onClick={onCompanyClick ? handleCompanyClick : undefined}
              />
            ) : null}
          </TableCell>
        </div>
        {/* Expanded cell overlay when truncated */}
        {shouldExpand && (
          <TableCell
            className="absolute left-0 top-0 w-[150%] overflow-hidden rounded-xs border-highlight-default bg-white px-2"
            style={{ zIndex: 20 }}
          >
            {company ? (
              <Company
                company={company}
                tabIndex={-1}
                onClick={onCompanyClick ? handleCompanyClick : undefined}
              />
            ) : null}
          </TableCell>
        )}
      </>
    );

    // If no contactId, just render the cell without popover (read-only mode)
    if (!contactId) {
      return (
        <div ref={wrapperRef} className="w-full">
          {cellContent}
        </div>
      );
    }

    return (
      <div ref={wrapperRef} className="w-full">
        <EditableCompanyCell
          company={company}
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          onSelectCompany={handleSelectCompany}
          onCreateCompany={onCreateCompany}
        >
          {cellContent}
        </EditableCompanyCell>
      </div>
    );
  }),
);
